import { inject, Injectable } from '@angular/core';
import { KeycloakAuthClient } from '@app/core/auth/keycloak-auth-client';

/**
 * Local return-value shape kept compatible with the previous Firebase `HttpsCallableResult<T>`
 * (`.data`) so callers don't change. This is purely a frontend-side convenience — the wire
 * response from the Express APIs is the plain `TResponse` body, with no `{ result }` envelope.
 */
export interface CallResult<TResponse> {
    data: TResponse;
}

/**
 * Calls backend Express API endpoints over HTTP, attaching the Keycloak bearer token. Replaces
 * the Firebase `httpsCallable` transport; the `call()` shape is unchanged so callers don't change.
 */
@Injectable({
    providedIn: 'root',
})
export class ApiConnector {

    private readonly authClient: KeycloakAuthClient = inject(KeycloakAuthClient);

    /**
     * Calls a backend API endpoint.
     * @param path The API path, e.g. `/intern/act` or `/auth/login`.
     * @param request Additional request data.
     */
    public async call<
        TRequest = unknown,
        TResponse = unknown
    >(path: string, request?: TRequest): Promise<CallResult<TResponse>> {
        const result: TResponse = await this.authClient.postApi<TResponse>(path, request ?? null, true);
        return { data: result };
    }
}
